import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  Plane,
  Hotel,
  Car,
  Compass,
  UtensilsCrossed,
  Building2,
  Plus,
  MapPin,
  Calendar,
  Users,
  Phone,
  Mail
} from "lucide-react";

const serviceIcons = {
  flight: Plane,
  hotel: Hotel,
  transfer: Car,
  assistance: Users,
  tour: Compass,
  meal: UtensilsCrossed,
  conference: Building2,
  other: Plus
};

const roomTypeLabels = {
  sencilla: "Sencilla",
  doble: "Doble",
  triple: "Triple",
  cuadruple: "Cuádruple",
  suite: "Suite",
  suite_junior: "Suite Junior",
  suite_presidencial: "Suite Presidencial",
  villa: "Villa",
  bungalow: "Bungalow",
  familiar: "Familiar",
  deluxe: "Deluxe",
  superior: "Superior",
  estandar: "Estándar"
};

const flightTypeLabels = {
  roundtrip: "Viaje Redondo",
  oneway: "Solo Ida"
};

const coverageTypeLabels = {
  basica: "Básica",
  estandar: "Estándar",
  premium: "Premium",
  todo_riesgo: "Todo Riesgo",
  medica: "Médica",
  cancelacion: "Cancelación de Viaje"
};

const QuotePreviewPanel = ({ quote, settings, showInternalInfo = false }) => {
  // Función para convertir número a texto en español
  const numberToWords = (num) => {
    const units = ['', 'UN', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'];
    const teens = ['DIEZ', 'ONCE', 'DOCE', 'TRECE', 'CATORCE', 'QUINCE', 'DIECISÉIS', 'DIECISIETE', 'DIECIOCHO', 'DIECINUEVE'];
    const tens = ['', '', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
    const hundreds = ['', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS', 'SEISCIENTOS', 'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS'];

    if (num === 0) return 'CERO';
    if (num === 100) return 'CIEN';

    const convertGroup = (n) => {
      if (n === 0) return '';
      if (n < 10) return units[n];
      if (n < 20) return teens[n - 10];
      if (n < 30) {
        if (n === 20) return 'VEINTE';
        return 'VEINTI' + units[n % 10];
      }
      if (n < 100) {
        const t = Math.floor(n / 10);
        const u = n % 10;
        return tens[t] + (u ? ' Y ' + units[u] : '');
      }
      if (n === 100) return 'CIEN';
      if (n < 1000) {
        const h = Math.floor(n / 100);
        const rest = n % 100;
        return hundreds[h] + (rest ? ' ' + convertGroup(rest) : '');
      }
      return '';
    };

    const integer = Math.floor(num);
    const decimal = Math.round((num - integer) * 100);

    let result = '';

    if (integer >= 1000000) {
      const millions = Math.floor(integer / 1000000);
      const rest = integer % 1000000;
      if (millions === 1) {
        result = 'UN MILLÓN';
      } else {
        result = convertGroup(millions) + ' MILLONES';
      }
      if (rest > 0) {
        if (rest >= 1000) {
          const thousands = Math.floor(rest / 1000);
          const remainder = rest % 1000;
          if (thousands === 1) {
            result += ' MIL';
          } else {
            result += ' ' + convertGroup(thousands) + ' MIL';
          }
          if (remainder > 0) {
            result += ' ' + convertGroup(remainder);
          }
        } else {
          result += ' ' + convertGroup(rest);
        }
      }
    } else if (integer >= 1000) {
      const thousands = Math.floor(integer / 1000);
      const rest = integer % 1000;
      if (thousands === 1) {
        result = 'MIL';
      } else {
        result = convertGroup(thousands) + ' MIL';
      }
      if (rest > 0) {
        result += ' ' + convertGroup(rest);
      }
    } else {
      result = convertGroup(integer);
    }

    if (decimal > 0) {
      result += ' ' + decimal.toString().padStart(2, '0') + '/100';
    } else {
      result += ' 00/100';
    }

    return result.trim();
  };

  const getCurrencyName = (currency) => {
    const names = {
      'MXN': 'MONEDA NACIONAL',
      'USD': 'DÓLARES AMERICANOS',
      'EUR': 'EUROS'
    };
    return names[currency] || currency;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const calculateExpirationDate = (createdAt, validityDays) => {
    const date = createdAt ? new Date(createdAt) : new Date();
    date.setDate(date.getDate() + (validityDays || 15));
    return date.toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  // Get current date for new quotes
  const quoteDate = quote?.created_at ? new Date(quote.created_at) : new Date();
  const quoteDateFormatted = quoteDate.toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: quote?.currency || "USD",
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const totalPassengers = (quote?.passengers?.adults || 0) + 
                          (quote?.passengers?.children || 0) + 
                          (quote?.passengers?.infants || 0);

  // Get service display info based on type - structured format
  const renderServiceDetails = (service) => {
    const type = service.type;
    const details = [];

    if (type === "flight") {
      // Ruta
      if (service.flight_origin || service.flight_destination) {
        details.push({
          label: "Ruta",
          value: `${service.flight_origin || "—"} → ${service.flight_destination || "—"}`
        });
      }
      // Tipo de vuelo
      if (service.flight_type) {
        details.push({
          label: "Tipo",
          value: flightTypeLabels[service.flight_type] || service.flight_type
        });
      }
      // Fecha Salida
      if (service.flight_start_date) {
        details.push({
          label: "Fecha Salida",
          value: formatDate(service.flight_start_date)
        });
      }
      // Fecha Regreso (solo si es roundtrip)
      if (service.flight_type !== "oneway" && service.flight_end_date) {
        details.push({
          label: "Fecha Regreso",
          value: formatDate(service.flight_end_date)
        });
      }
      // Pasajeros
      if (service.flight_pax > 0) {
        details.push({
          label: "Pasajeros",
          value: `${service.flight_pax} PAX`
        });
      }
      // Precio Unitario (por PAX)
      if (service.flight_price_per_pax > 0) {
        details.push({
          label: "Precio Unitario",
          value: formatCurrency(service.flight_price_per_pax)
        });
      }
    } else if (type === "hotel") {
      // Tipo de Habitación
      if (service.hotel_room_type) {
        details.push({
          label: "Habitación",
          value: roomTypeLabels[service.hotel_room_type] || service.hotel_room_type
        });
      }
      // Cantidad de habitaciones
      if (service.hotel_room_count > 0) {
        details.push({
          label: "Cantidad",
          value: `${service.hotel_room_count} habitación${service.hotel_room_count !== 1 ? 'es' : ''}`
        });
      }
      // Fechas
      if (service.hotel_start_date) {
        details.push({
          label: "Check-in",
          value: formatDate(service.hotel_start_date)
        });
      }
      if (service.hotel_end_date) {
        details.push({
          label: "Check-out",
          value: formatDate(service.hotel_end_date)
        });
      }
      // Noches
      if (service.hotel_nights > 0) {
        details.push({
          label: "Noches",
          value: `${service.hotel_nights}`
        });
      }
      // Precio Unitario (por noche)
      if (service.hotel_price_per_night > 0) {
        details.push({
          label: "Precio Unitario",
          value: formatCurrency(service.hotel_price_per_night)
        });
      }
    } else if (type === "transfer") {
      // Tipo de vehículo
      if (service.transfer_vehicle_type) {
        details.push({
          label: "Vehículo",
          value: service.transfer_vehicle_type
        });
      }
      // Ruta
      if (service.transfer_origin || service.transfer_destination) {
        details.push({
          label: "Ruta",
          value: `${service.transfer_origin || "—"} → ${service.transfer_destination || "—"}`
        });
      }
      // Fecha Inicio
      if (service.transfer_start_date) {
        details.push({
          label: "Fecha Inicio",
          value: formatDate(service.transfer_start_date)
        });
      }
      // Fecha Fin
      if (service.transfer_end_date) {
        details.push({
          label: "Fecha Fin",
          value: formatDate(service.transfer_end_date)
        });
      }
      // Precio unitario
      const unitPrice = (service.unit_price || 0) / (service.quantity || 1);
      if (unitPrice > 0) {
        details.push({
          label: "Precio Unitario",
          value: formatCurrency(unitPrice)
        });
      }
    } else if (type === "assistance") {
      // Tipo de cobertura
      if (service.assistance_coverage_type) {
        details.push({
          label: "Cobertura",
          value: coverageTypeLabels[service.assistance_coverage_type] || service.assistance_coverage_type
        });
      }
      // Destino
      if (service.assistance_destination) {
        details.push({
          label: "Destino",
          value: service.assistance_destination
        });
      }
      // Fecha Inicio
      if (service.assistance_start_date) {
        details.push({
          label: "Fecha Inicio",
          value: formatDate(service.assistance_start_date)
        });
      }
      // Fecha Fin
      if (service.assistance_end_date) {
        details.push({
          label: "Fecha Fin",
          value: formatDate(service.assistance_end_date)
        });
      }
      // Precio unitario
      const unitPrice = (service.unit_price || 0) / (service.quantity || 1);
      if (unitPrice > 0) {
        details.push({
          label: "Precio Unitario",
          value: formatCurrency(unitPrice)
        });
      }
    } else {
      // Generic - Precio unitario
      const unitPrice = (service.unit_price || 0) / (service.quantity || 1);
      if (unitPrice > 0) {
        details.push({
          label: "Precio Unitario",
          value: formatCurrency(unitPrice)
        });
      }
    }

    return details;
  };

  return (
    <Card className="pdf-preview overflow-hidden" data-testid="quote-preview-panel" id="quote-pdf-content">
      {/* Header with gradient */}
      <div className="text-white p-8" style={{ background: 'linear-gradient(135deg, #00157f, #0037ff)' }}>
        <div className="flex items-start justify-between">
          <div>
            {settings?.logo_url && settings.logo_url.trim() !== '' ? (
              <div className="mb-4">
                <img 
                  src={settings.logo_url} 
                  alt="Logo" 
                  className="h-12 object-contain"
                  style={{ maxWidth: '180px' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            ) : (
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: '#04ccda' }}>
                  <Plane className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold" style={{ fontFamily: "'Mulish', sans-serif" }}>
                  {settings?.name || "PD Travel"}
                </span>
              </div>
            )}
            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: "'Great', cursive" }}>
              Cotización de Viaje
            </h1>
            <p className="text-slate-300 font-mono">
              {quote?.quote_number || "—"}
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div 
              style={{ 
                background: '#04ccda', 
                color: '#ffffff',
                padding: '8px 16px',
                borderRadius: '8px',
                fontSize: '18px',
                fontWeight: 'bold',
                display: 'inline-block',
                minWidth: '100px',
                textAlign: 'center'
              }}
            >
              <span style={{ color: '#ffffff', fontWeight: 'bold' }}>
                {quote?.category === "MICE" ? "MICE" : quote?.category === "LEISURE" ? "LEISURE" : (quote?.category || "LEISURE")}
              </span>
            </div>
            <p className="text-sm text-slate-200" style={{ marginTop: '8px' }}>
              {quote?.quote_type === "GRUPAL" ? "Viaje Grupal" : quote?.quote_type === "FIT" ? "FIT" : "Individual"}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-8 space-y-6" style={{ fontFamily: "'Mulish', sans-serif" }}>
        {/* Client & Agency Info */}
        <div className="grid grid-cols-2 gap-8">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#00157f' }}>
              Cliente
            </h3>
            {quote?.client_company && (
              <p className="font-bold text-lg text-slate-900">{quote.client_company}</p>
            )}
            {quote?.business_unit && (
              <p className="text-sm text-slate-600 font-medium">{quote.business_unit}</p>
            )}
            <p className={`${quote?.client_company ? 'text-slate-600' : 'font-semibold text-lg text-slate-900'}`}>
              {quote?.client_name || "—"}
            </p>
            {quote?.client_email && (
              <p className="text-sm text-slate-500 flex items-center gap-2 mt-2">
                <Mail className="w-3 h-3" />
                {quote.client_email}
              </p>
            )}
            {quote?.client_phone && (
              <p className="text-sm text-slate-500 flex items-center gap-2">
                <Phone className="w-3 h-3" />
                {quote.client_phone}
              </p>
            )}
            {/* Fechas de cotización */}
            <div className="mt-4 pt-3 border-t border-slate-200">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-slate-400 text-xs">Fecha de Cotización</p>
                  <p className="font-medium text-slate-700">{quoteDateFormatted}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-xs">Fecha de Vencimiento</p>
                  <p className="font-medium text-amber-600">{calculateExpirationDate(quote?.created_at, quote?.validity_days)}</p>
                </div>
              </div>
              {quote?.project_name && (
                <p className="font-bold mt-3 uppercase" style={{ color: '#0037ff', fontSize: '1.35rem' }}>
                  PROYECTO: {quote.project_name.toUpperCase()}
                </p>
              )}
            </div>
          </div>
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
              Agencia
            </h3>
            <p className="font-bold text-lg text-slate-900">{settings?.name || "PD Travel"}</p>
            {settings?.rfc && (
              <p className="text-sm text-slate-600">RFC: {settings.rfc}</p>
            )}
            <div className="mt-3 pt-3 border-t border-slate-200">
              <p className="text-slate-400 text-xs">Cotización Realizada por</p>
              <p className="font-medium text-slate-700">{quote?.assigned_user_name || quote?.user_name || "—"}</p>
            </div>
          </div>
        </div>

        <Separator />

        {/* Destination, Dates & Passengers */}
        <div className="grid grid-cols-4 gap-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
              <MapPin className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Destino</p>
              <p className="font-medium">{quote?.destination || "—"}</p>
              {quote?.origin && <p className="text-xs text-slate-400">Desde: {quote.origin}</p>}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Fecha Inicio</p>
              <p className="font-medium">{formatDate(quote?.start_date)}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Fecha Fin</p>
              <p className="font-medium">{formatDate(quote?.end_date)}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Pasajeros</p>
              <p className="font-medium">
                {totalPassengers} persona{totalPassengers !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        </div>

        <Separator />

        {/* Services with detailed info and IVA */}
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: '#00157f' }}>
            Servicios Incluidos
          </h3>
          {quote?.services?.length > 0 ? (
            <div className="space-y-3">
              {quote.services.map((service, index) => {
                const Icon = serviceIcons[service.type] || Plus;
                const details = renderServiceDetails(service);
                const serviceIvaPercentage = service.service_iva_percentage ?? 16;
                const serviceIvaAmount = service.service_iva_amount || (service.unit_price * (serviceIvaPercentage / 100));
                const priceWithIva = (service.unit_price || 0) + serviceIvaAmount;
                
                return (
                  <div
                    key={service.id || index}
                    className="p-4 rounded-lg"
                    style={{ background: '#f8fafc', borderLeft: '4px solid #0093e8' }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm flex-shrink-0" style={{ border: '1px solid #e2e8f0' }}>
                          <Icon className="w-5 h-5" style={{ color: '#1d6f80' }} />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-slate-900">
                            {service.description || service.provider || service.type}
                          </p>
                          {service.provider && service.description && (
                            <p className="text-sm text-slate-500">{service.provider}</p>
                          )}
                          
                          {/* Service Details - Structured */}
                          {details.length > 0 && (
                            <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
                              {details.map((detail, idx) => (
                                <div key={idx} className="text-sm">
                                  <span className="text-slate-400">{detail.label}: </span>
                                  <span className="text-slate-700 font-medium">{detail.value}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-4">
                        <p className="text-sm text-slate-500">{formatCurrency(service.unit_price || 0)}</p>
                        <p className="text-xs text-slate-400">IVA {serviceIvaPercentage}%: {formatCurrency(serviceIvaAmount)}</p>
                        <p className="font-bold text-lg mt-1" style={{ color: '#00157f' }}>{formatCurrency(priceWithIva)}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-slate-400 text-center py-4">
              No hay servicios agregados
            </p>
          )}
        </div>

        <Separator />

        {/* Totals with IVA */}
        <div className="rounded-xl p-6 space-y-3" style={{ background: 'linear-gradient(135deg, #00157f, #0037ff)' }}>
          <div className="flex justify-between items-center text-white">
            <span className="text-cyan-200">Subtotal</span>
            <span className="text-xl font-semibold">{formatCurrency(quote?.subtotal || quote?.total || 0)}</span>
          </div>
          {quote?.iva_amount > 0 && (
            <div className="flex justify-between items-center text-cyan-200">
              <span>IVA</span>
              <span className="font-semibold">{formatCurrency(quote?.iva_amount || 0)}</span>
            </div>
          )}
          <Separator className="bg-blue-400/30" />
          <div className="flex justify-between items-center text-white">
            <span className="text-lg">Total</span>
            <span className="text-3xl font-bold" style={{ color: '#04ccda' }}>
              {formatCurrency(quote?.total || 0)}
            </span>
          </div>
          <div className="text-center pt-2 border-t border-blue-400/30">
            <p className="text-xs text-cyan-200 uppercase tracking-wide">
              ({numberToWords(quote?.total || 0)} {getCurrencyName(quote?.currency)})
            </p>
          </div>
        </div>

        {/* Terms */}
        {quote?.terms && (
          <div className="mt-8">
            <h3 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: '#00157f' }}>
              Términos y Condiciones
            </h3>
            <div className="text-sm text-slate-600 whitespace-pre-line p-4 rounded-lg" style={{ background: '#f8fafc', lineHeight: '1.6' }}>
              {quote.terms}
            </div>
          </div>
        )}

        {/* Validity */}
        <div className="text-center text-sm text-slate-500 pt-4 border-t">
          <p>
            Esta cotización es válida por <strong>{quote?.validity_days || 15} días</strong> (vence el <strong>{calculateExpirationDate(quote?.created_at, quote?.validity_days)}</strong>)
          </p>
          {settings?.phone && (
            <p className="mt-2">
              Contacto: {settings.phone} | {settings.email}
            </p>
          )}
        </div>
      </div>
    </Card>
  );
};

export default QuotePreviewPanel;
